import type { ISODate } from './types'

/**
 * 日期工具。全部基于 'YYYY-MM-DD' 字符串。
 *
 * 为什么不用 Date 做算术：本地时区的 Date 在夏令时切换和跨时区时会把
 * 「加一天」算成 23 或 25 小时，周期天数就会莫名其妙差一天。这里所有
 * 转换都走 UTC 正午，纯整数运算，不存在这个问题。
 */

const MS_PER_DAY = 86_400_000

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/

export function isISODate(s: unknown): s is ISODate {
  if (typeof s !== 'string' || !ISO_RE.test(s)) return false
  // 排除 2026-02-30 这种格式合法但不存在的日期
  return fromDayNumber(toDayNumber(s)) === s
}

/** 距 1970-01-01 的天数。ISO 日期 ↔ 整数的双射，所有算术都在整数域里做。 */
export function toDayNumber(d: ISODate): number {
  const y = Number(d.slice(0, 4))
  const m = Number(d.slice(5, 7))
  const day = Number(d.slice(8, 10))
  return Math.round(Date.UTC(y, m - 1, day) / MS_PER_DAY)
}

export function fromDayNumber(n: number): ISODate {
  const dt = new Date(n * MS_PER_DAY)
  const y = String(dt.getUTCFullYear()).padStart(4, '0')
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const d = String(dt.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function addDays(d: ISODate, n: number): ISODate {
  return fromDayNumber(toDayNumber(d) + n)
}

/** b - a，单位天。b 在 a 之后为正。 */
export function diffDays(a: ISODate, b: ISODate): number {
  return toDayNumber(b) - toDayNumber(a)
}

export function minDate(a: ISODate, b: ISODate): ISODate {
  return toDayNumber(a) <= toDayNumber(b) ? a : b
}

export function maxDate(a: ISODate, b: ISODate): ISODate {
  return toDayNumber(a) >= toDayNumber(b) ? a : b
}

export function isBetween(d: ISODate, start: ISODate, end: ISODate): boolean {
  const n = toDayNumber(d)
  return n >= toDayNumber(start) && n <= toDayNumber(end)
}

/** 闭区间 [start, end] 的所有日期。 */
export function rangeDays(start: ISODate, end: ISODate): ISODate[] {
  const out: ISODate[] = []
  const a = toDayNumber(start)
  const b = toDayNumber(end)
  for (let n = a; n <= b; n++) out.push(fromDayNumber(n))
  return out
}

/** 校验是否为合法的 IANA 时区名（如 'America/Chicago'）。 */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** 指定时区下的「今天」。服务端按家庭时区算，前端不自己猜。 */
export function todayIn(timezone: string): ISODate {
  return formatInTimeZone(new Date(), timezone)
}

/**
 * 非法时区退回 UTC，而不是抛异常。
 *
 * Intl 遇到不认识的时区名会抛 RangeError。这个函数在每次读取快照时都会调用，
 * 一旦数据库里存了个坏值（比如把 'America/Chicago' 写成了 'Chicago'），
 * 整个 app 就会 500，连进设置页去改都做不到 —— 死锁在一个自己修不了的状态里。
 * 日期算差几小时是小事，登不进去是大事。
 */
export function formatInTimeZone(instant: Date, timezone: string): ISODate {
  const zone = isValidTimeZone(timezone) ? timezone : 'UTC'
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** 0=周日 .. 6=周六 */
export function weekday(d: ISODate): number {
  return new Date(toDayNumber(d) * MS_PER_DAY).getUTCDay()
}

export function startOfMonth(d: ISODate): ISODate {
  return `${d.slice(0, 7)}-01`
}

export function endOfMonth(d: ISODate): ISODate {
  const y = Number(d.slice(0, 4))
  const m = Number(d.slice(5, 7))
  // 下个月第 0 天 = 本月最后一天
  return fromDayNumber(Math.round(Date.UTC(y, m, 0) / MS_PER_DAY))
}

export function addMonths(d: ISODate, n: number): ISODate {
  const y = Number(d.slice(0, 4))
  const m = Number(d.slice(5, 7))
  const day = Number(d.slice(8, 10))
  const target = new Date(Date.UTC(y, m - 1 + n, 1))
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate()
  // 1月31日 + 1个月 落到 2月28/29，而不是溢出到 3 月
  return fromDayNumber(
    Math.round(
      Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(day, lastDay)) /
        MS_PER_DAY,
    ),
  )
}

/**
 * 月历网格：从当月 1 号所在周的第一天开始，铺满整周数（6 周 = 42 格，
 * 固定高度，切换月份时不会抖动）。
 *
 * weekStart 是语言决定的（0 = 周日起，1 = 周一起）：中文和西语的日历从周一
 * 排，英文从周日排。默认周一，和表头 t.calendar.weekdays 必须来自同一份字典，
 * 否则日期会整体错一列。
 */
export function monthGrid(monthAnchor: ISODate, weekStart: 0 | 1 = 1): ISODate[] {
  const first = startOfMonth(monthAnchor)
  const back = (weekday(first) - weekStart + 7) % 7
  const gridStart = addDays(first, -back)
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i))
}
